import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Trash component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconTrash extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M16.76 5.88145H21.48C21.89 5.88145 22.23 6.22145 22.23 6.63145C22.23 7.04145 21.89 7.38145 21.48 7.38145H19.89V20.6814C19.89 21.2914 19.65 21.8814 19.21 22.3114C18.77 22.7414 18.19 22.9914 17.58 22.9914H6.65C6.04 22.9914 5.45 22.7514 5.02 22.3114C4.59 21.8814 4.34 21.2914 4.34 20.6814V7.38145H2.75C2.34 7.38145 2 7.04145 2 6.63145C2 6.22145 2.34 5.88145 2.75 5.88145H7.47C7.47 3.31145 9.55 1.23145 12.11 1.23145C14.67 1.23145 16.76 3.31145 16.76 5.88145ZM15.26 5.88145C15.26 4.14145 13.85 2.73145 12.11 2.73145C10.37 2.73145 8.96 4.14145 8.96 5.88145H15.25H15.26ZM5.84 7.38145H18.39V20.6814C18.39 20.8914 18.3 21.1014 18.15 21.2514C18 21.4014 17.79 21.4914 17.58 21.4914H6.65C6.44 21.4914 6.23 21.4014 6.08 21.2514C5.93 21.1014 5.84 20.8914 5.84 20.6814V7.38145ZM10.52 11.3114C10.52 10.9014 10.18 10.5614 9.77 10.5614C9.36 10.5614 9.02 10.9014 9.02 11.3114V17.5614C9.02 17.9714 9.36 18.3114 9.77 18.3114C10.18 18.3114 10.52 17.9714 10.52 17.5614V11.3114V11.3114ZM14.45 10.5614C14.86 10.5614 15.2 10.9014 15.2 11.3114V17.5614C15.2 17.9714 14.86 18.3114 14.45 18.3114C14.04 18.3114 13.7 17.9714 13.7 17.5614V11.3114C13.7 10.9014 14.04 10.5614 14.45 10.5614V10.5614Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-trash') === undefined) {
  customElements.define('cat-icon-trash', BlocksIconTrash);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-trash': BlocksIconTrash;
  }
}
